// Cookie-backed token storage for Wix's OAuthStrategy (browser).
//
// Default OAuthStrategy keeps tokens in memory only, so they're lost on every
// navigation and the cart resets each page load. Persisting in a cookie makes
// the visitor session sticky. NOT HttpOnly — the Wix SDK runs in the browser
// and must read tokens to build the Authorization header. SameSite=Lax so the
// post-checkout redirect back from Wix's hosted checkout keeps the session.

'use client';

import type { TokenStorage, Tokens } from '@wix/sdk';

const COOKIE_NAME = 'mc_session';
const COOKIE_MAX_AGE_DAYS = 30;

const EMPTY_TOKENS: Tokens = {
  accessToken: { value: '', expiresAt: 0 },
  refreshToken: { value: '', role: 'none' as Tokens['refreshToken']['role'] },
};

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const target = name + '=';
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(target)) return decodeURIComponent(part.slice(target.length));
  }
  return null;
}

function writeCookie(name: string, value: string, days: number) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 86_400_000).toUTCString();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
}

export const cookieTokenStorage: TokenStorage = {
  getTokens(): Tokens {
    const raw = readCookie(COOKIE_NAME);
    if (!raw) return EMPTY_TOKENS;
    try {
      const parsed = JSON.parse(raw) as Tokens;
      if (!parsed?.accessToken?.value || !parsed?.refreshToken?.value) return EMPTY_TOKENS;
      return parsed;
    } catch {
      return EMPTY_TOKENS;
    }
  },
  setTokens(tokens: Tokens): void {
    writeCookie(COOKIE_NAME, JSON.stringify(tokens), COOKIE_MAX_AGE_DAYS);
  },
};
